import {
  Bot,
  ClipboardPen,
  FileSearch,
  FileText,
  FolderOpen,
  LayoutDashboard,
  MessageSquare,
  ReceiptText,
  Shield,
  ShieldCheck,
  Truck,
  UsersRound,
  Vault,
} from 'lucide-react'
import * as React from 'react'
import { Link, useLocation } from 'react-router'

import { TrailerIcon } from '@/components/icons/TrailerIcon'
import { TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  CUSTOMER_DEFAULT_TAB,
  CUSTOMER_FILL_APPLICATION_TAB,
  CUSTOMER_QUOTES_TAB,
  customerTabHref,
} from '@/lib/customerTabs'

interface CustomerTab {
  value: string
  label: string
  icon: React.ReactNode
}

export { CUSTOMER_DEFAULT_TAB, CUSTOMER_QUOTES_TAB }

const CUSTOMER_TABS: CustomerTab[] = [
  { value: CUSTOMER_DEFAULT_TAB, icon: <LayoutDashboard />, label: 'Overview' },
  { value: 'documents', icon: <FileText />, label: 'Documents' },
  { value: 'vault', icon: <Vault />, label: 'Vault' },
  { value: 'coi', icon: <ShieldCheck />, label: 'COI' },
  { value: CUSTOMER_FILL_APPLICATION_TAB, icon: <ClipboardPen />, label: 'Intake' },
  { value: 'application', icon: <MessageSquare />, label: 'Application' },
  { value: CUSTOMER_QUOTES_TAB, icon: <ReceiptText />, label: 'Quotes' },
  { value: 'insurance', icon: <Shield />, label: 'Insurance' },
  { value: 'loss-runs', icon: <FileSearch />, label: 'Loss Runs' },
  { value: 'trucks', icon: <Truck />, label: 'Trucks' },
  { value: 'trailers', icon: <TrailerIcon />, label: 'Trailers' },
  { value: 'drivers', icon: <UsersRound />, label: 'Drivers' },
  { value: 'claims', icon: <FolderOpen />, label: 'Claims' },
  { value: 'agent-chat', icon: <Bot />, label: 'Agent' },
]

/** A place on the customer page with no pill of its own: the console's policy change form. */
export const CUSTOMER_POLICY_CHANGES_TAB = 'policy-changes'

export const CUSTOMER_TAB_VALUES = [...CUSTOMER_TABS.map((tab) => tab.value), CUSTOMER_POLICY_CHANGES_TAB]

/**
 * The customer page's tabs down the left rail — real links, so a tab opens in a new tab.
 * Below `lg` the rail has no room, so the same list scrolls sideways above the content.
 */
export function CustomerNav({ orgId }: { orgId: string }) {
  const location = useLocation()
  const listRef = React.useRef<HTMLDivElement>(null)

  // Rects, not offsetLeft: the strip is not the offsetParent. Own scrollLeft, since scrollIntoView drags the page too.
  React.useEffect(() => {
    const list = listRef.current
    const active = list?.querySelector<HTMLElement>('[data-state="active"]')
    if (!list || !active) return
    const listBox = list.getBoundingClientRect()
    const activeBox = active.getBoundingClientRect()
    if (activeBox.left < listBox.left) {
      list.scrollLeft -= listBox.left - activeBox.left
    } else if (activeBox.right > listBox.right) {
      list.scrollLeft += activeBox.right - listBox.right
    }
  }, [location.pathname])

  return (
    <TabsList
      ref={listRef}
      className="no-scrollbar h-fit w-full max-w-full justify-start gap-0.5 overflow-x-auto bg-transparent p-0 lg:items-stretch lg:overflow-visible"
    >
      {CUSTOMER_TABS.map((tab) => (
        <TabsTrigger
          key={tab.value}
          value={tab.value}
          asChild
          className="data-[state=active]:bg-brand-100 data-[state=active]:text-ink data-[state=inactive]:hover:bg-ink-50 h-auto flex-none shrink-0 justify-start gap-2 rounded-lg border-none px-2.5 py-1.5 data-[state=active]:shadow-none lg:w-full"
        >
          <Link
            to={{ pathname: customerTabHref(orgId, tab.value), search: location.search }}
            state={location.state}
            preventScrollReset
          >
            {tab.icon}
            {tab.label}
          </Link>
        </TabsTrigger>
      ))}
    </TabsList>
  )
}
